class ProductListComponent extends HTMLElement {
    constructor() {
      super();
      this.productGids = [];
      this.products = [];
      this.isLoggedIn = false;
    }
    
    // Get configuration from attributes with fallbacks
    get shopDomain() {
      return this.getAttribute('shop-domain') || window.Shopify?.shop || '';
    }
    
    get storefrontToken() {
      return this.getAttribute('storefront-token') || '';
    }
    
    get loggedIn() {
      // Only use the explicit logged-in attribute
      const loggedInAttr = this.getAttribute('logged-in');
      return loggedInAttr === 'true';
    }
    
    get metafieldsConfig() {
      const attr = this.getAttribute('metafields');
      if (!attr) return [];
      try {
        return JSON.parse(attr);
      } catch {
        return [];
      }
    }
    
    connectedCallback() {
      // Set login status using only the attribute
      this.isLoggedIn = this.loggedIn;
      
      console.log('Login status detected:', this.isLoggedIn);
      
      // If user is not logged in, show login button
      if (!this.isLoggedIn) {
        this.renderLoginButton();
        return;
      }
      
      // Get product GIDs and load product data
      this.loadProductData();
    }
    
    renderLoginButton() {
      this.innerHTML = `
        <div class="nf-bg-white">
          <div class="nf-mx-auto nf-max-w-2xl nf-px-4 nf-py-16 sm:nf-px-6 sm:nf-py-24 lg:nf-max-w-7xl lg:nf-px-8">
            <div class="nf-text-center">
              <p class="nf-mb-4 nf-text-lg nf-text-gray-700">Please log in to view your product list</p>
              <a href="/account/login" class="nf-inline-flex nf-items-center nf-justify-center nf-px-5 nf-py-3 nf-border nf-border-transparent nf-text-base nf-font-medium nf-rounded-md nf-text-white nf-bg-indigo-600 hover:nf-bg-indigo-700">
                Log in
              </a>
            </div>
          </div>
        </div>
      `;
    }
    
    async loadProductData() {
      try {
        // Check if product data is explicitly provided via attribute
        const productDataAttr = this.getAttribute('customer-product');
        
        if (productDataAttr) {
          // Handle both JSON string and array cases
          try {
            // First try parsing as JSON
            this.productGids = JSON.parse(productDataAttr);
          } catch (e) {
            // If parsing fails, check if it's already an array
            if (Array.isArray(productDataAttr)) {
              this.productGids = productDataAttr;
            } else {
              // If it's neither valid JSON nor an array, log error and render empty state
              console.error('Invalid customer-product format:', e);
              this.renderEmptyState();
              return;
            }
          }
        } else {
          this.renderEmptyState();
          return;
        }
        
        // Validate shop domain and token
        if (!this.shopDomain || !this.storefrontToken) {
          console.error('Missing shop-domain or storefront-token attributes');
          this.renderError('Configuration error: Missing shop domain or storefront token');
          return;
        }
        
        // Once we have the GIDs, fetch product data
        if (this.productGids && this.productGids.length > 0) {
          await this.fetchProductData();
        } else {
          this.renderEmptyState();
        }
      } catch (error) {
        console.error('Error loading product data:', error);
        this.renderError();
      }
    }
    
    async fetchProductData() {
      try {
        if (!this.productGids || this.productGids.length === 0) {
          this.renderEmptyState();
          return;
        }
        
        // Fetch all products in a single query
        const products = await this.fetchProductsByGids(this.productGids);
        
        this.products = products;
        
        if (products.length > 0) {
          this.renderProducts();
        } else {
          this.renderEmptyState();
        }
      } catch (error) {
        console.error('Error fetching product data:', error);
        this.renderError();
      }
    }
    
    async fetchProductsByGids(gids) {
      // Build metafield identifiers for the query
      const metafields = this.metafieldsConfig;
      const metafieldIdentifiers = metafields && metafields.length > 0
        ? `metafields(identifiers: [${metafields.map(
          /** @param {{namespace: string, key: string}} mf */
          mf => `{namespace: \"${mf.namespace}\", key: \"${mf.key}\"}`
        ).join(', ')}]) {
            namespace
            key
            value
            type
          }`
        : '';
      // Create a GraphQL query that fetches multiple products at once
      const query = `
        query GetProducts($ids: [ID!]!) {
          nodes(ids: $ids) {
            ... on Product {
              id
              title
              handle
              onlineStoreUrl
              priceRange {
                minVariantPrice {
                  amount
                  currencyCode
                }
              }
              compareAtPriceRange {
                minVariantPrice {
                  amount
                  currencyCode
                }
              }
              featuredImage {
                url
                altText
              }
              variants(first: 1) {
                nodes {
                  sku
                }
              }
              ${metafieldIdentifiers}
            }
          }
        }
      `;
      
      try {
        console.log(`Fetching ${gids.length} products in batch`);
        
        const response = await fetch(`https://${this.shopDomain}/api/2025-07/graphql.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.storefrontToken
          },
          body: JSON.stringify({
            query,
            variables: {
              ids: gids
            }
          })
        });
        
        const result = await response.json();
        console.log(result)
        
        if (result.errors) {
          console.error('GraphQL errors:', result.errors);
          console.error('Query used:', query);
          console.error('Variables:', { ids: gids });
          return [];
        }
        
        if (!result.data || !result.data.nodes) {
          console.error('No product data returned');
          return [];
        }
        
        // Filter out any null responses (which can happen if a product is not found)
        return result.data.nodes.filter(node => node && node.id);
      } catch (error) {
        console.error(`Error in fetchProductsByGids:`, error);
        return [];
      }
    }

    // Helper to fetch and cache referenced products
    /**
     * @type {{ [gid: string]: any }}
     */
    referencedProductCache = {};
    /**
     * Fetch and cache referenced products by GID
     * @param {string[]} gids
     * @returns {Promise<any[]>}
     */
    async fetchReferencedProducts(gids) {
      const missing = gids.filter((/** @type {string} */ gid) => !this.referencedProductCache[gid]);
      if (missing.length === 0) {
        return gids.map((/** @type {string} */ gid) => this.referencedProductCache[gid]);
      }
      const products = await this.fetchProductsByGids(missing);
      products.forEach((/** @type {any} */ p) => {
        this.referencedProductCache[p.id] = p;
      });
      return gids.map((/** @type {string} */ gid) => this.referencedProductCache[gid]).filter(Boolean);
    }
    
    renderProducts() {
      const metafields = this.metafieldsConfig;
      const html = `
        <div class="nf-bg-white">
          <div class="nf-mx-auto nf-max-w-2xl nf-px-4 nf-py-16 sm:nf-px-6 sm:nf-py-24 lg:nf-max-w-7xl lg:nf-px-8">
            <h2 class="nf-sr-only">Products</h2>
  
            <div class="nf-grid nf-grid-cols-1 nf-gap-x-6 nf-gap-y-10 sm:nf-grid-cols-2 lg:nf-grid-cols-3 xl:nf-grid-cols-4 xl:nf-gap-x-8">
              ${this.products.map(product => {
                // Render metafields if present
                let metafieldsHtml = '';
                if (product.metafields && Array.isArray(product.metafields) && product.metafields.length > 0) {
                  metafieldsHtml = `<div class="nf-mt-2 nf-space-y-1">` +
                    product.metafields.map(
                      /**
                       * @param {{key: string, value: string, type: string, namespace?: string}} mf
                       * @param {number} idx
                       */
                      (mf, idx) => {
                        if (!mf || !mf.value) return '';
                        let valueHtml = '';
                        // Support single line text metafields
                        if (mf.type === 'single_line_text_field') {
                          valueHtml = `<span>${mf.value}</span>`;
                        } else if (mf.type === 'list.product_reference' || mf.type === 'product_reference') {
                          // Render product reference(s) as cards
                          let gids = [];
                          try {
                            gids = JSON.parse(mf.value);
                            if (!Array.isArray(gids)) gids = [gids];
                          } catch {
                            gids = [mf.value];
                          }
                          // Render a placeholder, will be replaced after fetch
                          valueHtml = `<div class="nf-product-ref-list" data-gids="${gids.join(',')}">Loading referenced products...</div>`;
                          // After render, fetch and render referenced products
                          setTimeout(async () => {
                            const refProducts = await this.fetchReferencedProducts(gids);
                            const refHtml = refProducts.map(refProduct => this.renderProductCard(refProduct)).join('');
                            const el = this.querySelector(`.nf-product-ref-list[data-gids='${gids.join(',')}']`);
                            if (el) el.innerHTML = refHtml;
                          }, 0);
                        } else {
                        // Try to parse as JSON array of strings or objects for any type
                        let parsed;
                        try {
                          parsed = JSON.parse(mf.value);
                        } catch {}
                        if (Array.isArray(parsed) && parsed.every(item => typeof item === 'string')) {
                          valueHtml = `<span>${parsed.join(', ')}</span>`;
                        } else if (Array.isArray(parsed) && parsed.every(item => typeof item === 'object' && item !== null && !Array.isArray(item))) {
                          // Array of objects: render each object as key-value pairs
                          valueHtml = `<div class="nf-space-y-1">` + parsed.map(obj => {
                            return Object.entries(obj)
                              .map(([k, v]) => `${formatMetafieldLabel(k)}: ${v}`)
                              .join(', ');
                          }).join('<br/>') + `</div>`;
                        } else if (mf.type === 'json') {
                          try {
                            const json = parsed !== undefined ? parsed : JSON.parse(mf.value);
                            valueHtml = `<pre class="nf-bg-gray-50 nf-rounded nf-p-2 nf-text-xs nf-overflow-x-auto">${JSON.stringify(json, null, 2)}</pre>`;
                          } catch {
                            valueHtml = `<span>${mf.value}</span>`;
                          }
                        } else if (mf.type === 'list.product_reference') {
                          try {
                            const arr = parsed !== undefined ? parsed : JSON.parse(mf.value);
                            valueHtml = `<span>Product list: ${Array.isArray(arr) ? arr.length + ' products' : mf.value}</span>`;
                          } catch {
                            valueHtml = `<span>Product list: ${mf.value}</span>`;
                          }
                        } else if (mf.type === 'multi_line_text_field') {
                          valueHtml = `<div class="nf-text-xs nf-text-gray-500">${mf.value.replace(/\n/g, '<br/>')}</div>`;
                        } else {
                          valueHtml = `<span class="nf-text-xs nf-text-gray-500">${mf.value}</span>`;
                        }
                        }
                        // Find the metafield config for this key
                        const config = (this.metafieldsConfig || []).find(
                          /** @param {{key: string, namespace?: string, label?: boolean}} c */
                          c => c.key === mf.key && c.namespace === mf.namespace
                        );
                        // If config.label is true, show label; otherwise, hide label (default: hide)
                        const showLabel = config && typeof config.label === 'boolean' ? config.label : false;
                        let label = formatMetafieldLabel(mf.key);
                        return showLabel
                          ? `<div class="nf-flex nf-items-center nf-gap-1"><span class="nf-font-semibold">${label}:</span> ${valueHtml}</div>`
                          : `<div class="nf-flex nf-items-center nf-gap-1">${valueHtml}</div>`;
                      }
                    ).join('') + `</div>`;
                }
                return `
                <a href="${product.onlineStoreUrl || '/products/' + product.handle}" class="nf-group" data-product-id="${product.id}">
                  ${product.featuredImage 
                    ? `<img 
                        src="${product.featuredImage.url}" 
                        alt="${product.featuredImage.altText || ''}" 
                        class="nf-mb-[1rem] nf-aspect-square nf-w-full nf-rounded-lg nf-bg-gray-200 nf-object-cover group-hover:nf-opacity-75 xl:nf-aspect-[7/8]" 
                        loading="lazy"
                      />`
                    : `<div class="nf-aspect-square nf-w-full nf-rounded-lg nf-bg-gray-200 nf-flex nf-items-center nf-justify-center xl:nf-aspect-[7/8]">
                        <span class="nf-text-gray-400">No image</span>
                      </div>`
                  }
                  <h3 class="nf-text-sm nf-text-gray-700">${product.title}</h3>
                  <p class="nf-mt-1 nf-text-lg nf-font-medium nf-text-gray-900">
                    ${this.formatMoney(product.priceRange.minVariantPrice.amount, product.priceRange.minVariantPrice.currencyCode)}
                  </p>
                  ${product.compareAtPriceRange && product.compareAtPriceRange.minVariantPrice.amount > 0 && 
                    product.compareAtPriceRange.minVariantPrice.amount > product.priceRange.minVariantPrice.amount
                    ? `<p class="nf-mt-1 nf-text-sm nf-text-gray-500 nf-line-through">
                        ${this.formatMoney(product.compareAtPriceRange.minVariantPrice.amount, product.compareAtPriceRange.minVariantPrice.currencyCode)}
                      </p>`
                    : ''
                  }
                  ${product.variants && product.variants.nodes && product.variants.nodes[0] && product.variants.nodes[0].sku
                    ? `<p class="nf-mt-1 nf-text-xs nf-text-gray-400 nf-hidden">SKU: ${product.variants.nodes[0].sku}</p>`
                    : ''
                  }
                  ${metafieldsHtml}
                </a>
                `;
              }).join('')}
            </div>
          </div>
        </div>
      `;
      
      this.innerHTML = html;
    }

    // Helper to render a product card (for referenced products)
    /**
     * Render a product card for a referenced product
     * @param {any} product
     * @returns {string}
     */
    renderProductCard(product) {
      if (!product) return '';
      const productUrl = product.onlineStoreUrl || ('/products/' + product.handle);
      return `
        <a href="${productUrl}" class="nf-block nf-no-underline hover:nf-bg-gray-100 nf-transition-colors nf-duration-150 nf-border nf-rounded nf-p-2 nf-mb-2 nf-bg-gray-50 nf-flex nf-items-center nf-gap-2" target="_blank" rel="noopener">
          ${product.featuredImage 
            ? `<img src="${product.featuredImage.url}" alt="${product.featuredImage.altText || ''}" class="nf-w-12 nf-h-12 nf-object-cover nf-rounded" loading="lazy" />`
            : `<div class="nf-w-12 nf-h-12 nf-bg-gray-200 nf-flex nf-items-center nf-justify-center nf-rounded"><span class="nf-text-gray-400 nf-text-xs">No image</span></div>`
          }
          <div class="nf-flex-1">
            <div class="nf-font-semibold nf-text-xs nf-text-green-600 nf-mb-1">Alternative products</div>
            <div class="nf-font-medium nf-text-sm">${product.title}</div>
            <div class="nf-text-xs nf-text-gray-500">
              ${product.variants && product.variants.nodes && product.variants.nodes[0] && product.variants.nodes[0].sku ? `SKU: ${product.variants.nodes[0].sku}` : ''}
            </div>
          </div>
        </a>
      `;
    }
    
    renderEmptyState() {
      this.innerHTML = `
        <div class="nf-bg-white">
          <div class="nf-mx-auto nf-max-w-2xl nf-px-4 nf-py-16 sm:nf-px-6 sm:nf-py-24 lg:nf-max-w-7xl lg:nf-px-8">
            <h2 class="nf-sr-only">Products</h2>
            <p class="nf-text-center nf-py-8 nf-text-gray-500">No products found</p>
          </div>
        </div>
      `;
    }
    
    renderError(message = 'Failed to load products') {
      this.innerHTML = `
        <div class="nf-bg-white">
          <div class="nf-mx-auto nf-max-w-2xl nf-px-4 nf-py-16 nf-sm:nf-px-6 nf-sm:nf-py-24 nf-lg:nf-max-w-7xl nf-lg:nf-px-8">
            <h2 class="nf-sr-only">Products</h2>
            <p class="nf-text-center nf-py-8 nf-text-red-500">${message}</p>
          </div>
        </div>
      `;
    }
    
    formatMoney(amount, currencyCode = 'USD') {
      // Format money based on the currency code
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode
      }).format(amount);
    }
    
    disconnectedCallback() {
      // Cleanup if needed
    }
  }
  
  // Register the custom element
  customElements.define('product-list', ProductListComponent);

  /**
   * @param {string} key
   * @returns {string}
   */
  function formatMetafieldLabel(key) {
    return key
      .replace(/[_-]+/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\b\w/g, /** @param {string} l */ l => l.toUpperCase());
  }